/**
 * Playground Run Comparison — side-by-side diff of two playground runs.
 *
 * Shows output, latency, cost, and token count differences for two selected
 * runs (e.g. same prompt through two different aliases, or the same alias at
 * two different times). Scoped to exactly 2 runs.
 */
import { useEffect, useRef, type JSX } from 'react'
import { GitCompare, Minus, TrendingDown, TrendingUp, X } from 'lucide-react'

interface CompareRun {
  id: string
  task: string | null
  alias: string | null
  prompt: string
  systemPrompt: string | null
  maxTokens: number | null
  status: string
  requestedProviderModel: string | null
  actualProviderModel: string | null
  usedFallback: boolean
  latencyMs: number | null
  promptTokens: number | null
  completionTokens: number | null
  estimatedCost: number | null
  output: string | null
  error: string | null
  createdAt: string
}

interface PlaygroundCompareDrawerProps {
  runA: CompareRun | null
  runB: CompareRun | null
  isOpen: boolean
  onClose: () => void
}

function MetricDelta({
  a,
  b,
  unit,
  higherIsBetter,
}: {
  a: number | null
  b: number | null
  unit?: string
  higherIsBetter: boolean
}): JSX.Element {
  if (a === null || b === null) return <span className="text-xs text-gray-400">—</span>
  const delta = b - a
  const isBetter = higherIsBetter ? delta > 0 : delta < 0
  const Icon = delta === 0 ? Minus : isBetter ? TrendingUp : TrendingDown
  const color = delta === 0 ? 'text-gray-400' : isBetter ? 'text-emerald-600' : 'text-red-600'
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${color}`}>
      <Icon className="h-3 w-3" />
      {delta > 0 ? '+' : ''}{delta.toFixed(Math.abs(delta) < 1 && delta !== 0 ? 6 : 2)}{unit ?? ''}
    </span>
  )
}

function RunColumn({ run, label }: { run: CompareRun; label: string }): JSX.Element {
  return (
    <div className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-gray-50/50 p-3">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label}</p>
      <p className="mt-0.5 font-mono text-[11px] text-gray-600">{run.actualProviderModel ?? run.requestedProviderModel ?? '—'}</p>
      <p className="font-mono text-[10px] text-gray-400">{run.alias ?? run.task ?? '—'} · {new Date(run.createdAt).toLocaleString()}</p>

      <div className="mt-3 space-y-1.5 text-xs">
        <div className="flex justify-between">
          <span className="text-gray-500">Latency</span>
          <span className="font-mono text-gray-900">{run.latencyMs ?? '—'}ms</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Tokens</span>
          <span className="font-mono text-gray-900">{run.promptTokens ?? 0} in / {run.completionTokens ?? 0} out</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Cost</span>
          <span className="font-mono text-gray-900">{run.estimatedCost !== null ? `$${run.estimatedCost.toFixed(6)}` : '—'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Fallback</span>
          <span className="font-mono text-gray-900">{run.usedFallback ? 'yes' : 'no'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Status</span>
          <span className={`font-mono ${run.status === 'success' ? 'text-emerald-600' : 'text-red-600'}`}>{run.status}</span>
        </div>
      </div>

      <div className="mt-3">
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Prompt</p>
        <pre className="max-h-24 overflow-auto whitespace-pre-wrap break-words rounded border border-gray-100 bg-white p-2 text-[11px] text-gray-700">
          {run.prompt}
        </pre>
      </div>

      <div className="mt-3">
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
          {run.status === 'success' ? 'Output' : 'Error'}
        </p>
        <pre className={`max-h-48 overflow-auto whitespace-pre-wrap break-words rounded border p-2 text-[11px] ${
          run.status === 'success' ? 'border-gray-100 bg-white text-gray-800' : 'border-red-100 bg-red-50/40 text-red-700'
        }`}>
          {run.status === 'success' ? (run.output ?? '—') : (run.error ?? '—')}
        </pre>
      </div>
    </div>
  )
}

export default function PlaygroundCompareDrawer({
  runA,
  runB,
  isOpen,
  onClose,
}: PlaygroundCompareDrawerProps): JSX.Element | null {
  const drawerRef = useRef<HTMLDivElement>(null)

  // Minimal escape handler
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }
    document.addEventListener('keydown', handler)
    return (): void => document.removeEventListener('keydown', handler)
  }, [isOpen, onClose])

  if (!isOpen || !runA || !runB) return null

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby="pg-compare-title">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div
        ref={drawerRef}
        className="absolute right-0 top-0 flex h-full w-full flex-col bg-white shadow-2xl md:w-[900px]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 p-4">
          <div className="flex min-w-0 items-center gap-2">
            <GitCompare className="h-4 w-4 shrink-0 text-[#3454cf]" />
            <span id="pg-compare-title" className="min-w-0 truncate text-sm font-semibold text-gray-900">
              Compare Playground Runs
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close drawer"
            className="rounded-sm p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 pb-6">
          {/* Delta summary */}
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-sm border border-gray-200 bg-white p-3 text-center shadow-sm">
              <span className="text-[10px] font-medium text-gray-500">Latency delta</span>
              <div className="mt-1"><MetricDelta a={runA.latencyMs} b={runB.latencyMs} unit="ms" higherIsBetter={false} /></div>
            </div>
            <div className="rounded-sm border border-gray-200 bg-white p-3 text-center shadow-sm">
              <span className="text-[10px] font-medium text-gray-500">Output tokens delta</span>
              <div className="mt-1"><MetricDelta a={runA.completionTokens} b={runB.completionTokens} higherIsBetter={false} /></div>
            </div>
            <div className="rounded-sm border border-gray-200 bg-white p-3 text-center shadow-sm">
              <span className="text-[10px] font-medium text-gray-500">Cost delta</span>
              <div className="mt-1"><MetricDelta a={runA.estimatedCost} b={runB.estimatedCost} unit="$" higherIsBetter={false} /></div>
            </div>
            <div className="rounded-sm border border-gray-200 bg-white p-3 text-center shadow-sm">
              <span className="text-[10px] font-medium text-gray-500">Same prompt?</span>
              <div className="mt-1">
                <span className={`text-xs font-medium ${runA.prompt === runB.prompt ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {runA.prompt === runB.prompt ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          </div>

          {/* Side-by-side */}
          <div className="flex gap-3">
            <RunColumn run={runA} label="Run A" />
            <RunColumn run={runB} label="Run B" />
          </div>
        </div>
      </div>
    </div>
  )
}
